import React, { useState } from 'react';
import { CrearCotizacionNBSVista } from '../vista/crearCotizacionNBSVista';
import { estadoInicialCotizacionNBS, PAIS_PANAMA_ID } from '../interfaces';
import type { ICotizacionNBS } from '../../../modelos/CotizadorNBS/entidades/ICotizacionNBS';
import type { IEquipoAscensor } from '../../../modelos/CotizadorNBS/entidades/IEquipoAscensor';
import type { IAscensor } from '../../../modelos/CotizadorNBS/entidades/IAscensor';
import type { IEscalera } from '../../../modelos/CotizadorNBS/entidades/IEscalera';
import type { IEquipoEscalera } from '../../../modelos/CotizadorNBS/entidades/IEquipoEscalera';
import { accionTransicionEstado, EnumEstadoCotizacionNBS, nombreEstadoCotizacion, type EstadoCotizacionNBSId } from '../../../modelos/CotizadorNBS/entidades/EstadoCotizacionNBS';
import type { IConfiguracionState } from '../../configuracionCotizadorNbs/interfaces';
import { useConfirmacion } from '../../../globales/contextos/confirmacionContext';
import { CotizadorModelo } from '../../../modelos/CotizadorNBS';
import type { IVariacionMensualMes } from '../../../modelos/CotizadorNBS/entidades/IVariacionMensualMes';
import { useAvisoErrorConsulta } from '../../configuracionCotizadorNbs/controladorVista/useAvisoErrorConsulta';
import { UbicacionModelo } from '../../../modelos/Ubicacion';
import { useAutenticacion } from '../../../globales/contextos/authContext';
import { useNotificaciones } from '../../../globales/contextos/notificationContext';
import { useCargando } from '../../../globales/contextos/cargandoContext';
import { EnumTipoNotificacion } from '../../../globales/interfaces';
import { monedaUsaTrm } from '../../configuracionCotizadorNbs/controladorVista/useMonedaPais';
import { usePdfCotizacion } from './usePdfCotizacion';
import { especificacionesAscensorDesdeApi } from '../utils/especificacionesAscensor';
import { especificacionesEscaleraDesdeApi } from '../utils/especificacionesEscalera';

/** Detalle del ascensor como lo recibe el back al agregar o editar el equipo. */
const detalleAscensorApi = (ascensor: IEquipoAscensor): IAscensor => ({
    Tipo: ascensor.Tipo,
    Uso: ascensor.Uso,
    Capacidad: ascensor.Capacidad,
    VelocidadIngreso: ascensor.VelocidadIngreso,
    OperadorPuertaId: Number(ascensor.OperadorPuerta),
    Paradas: ascensor.Paradas,
    MaquinaId: Number(ascensor.Maquina),
    PotenciaMotor: ascensor.PotenciaMotor,
    Especificaciones: ascensor.Especificaciones
});

/** Detalle de la escalera o rampa como lo recibe el back al agregar o editar el equipo. */
const detalleEscaleraApi = (escalera: IEquipoEscalera): IEscalera => ({
    Tipo: escalera.Tipo,
    Contenedores: escalera.Contenedores,
    Altura: escalera.Altura,
    VelocidadIngreso: escalera.VelocidadIngreso,
    AnchoPasos: escalera.AnchoPasos,
    Inclinacion: escalera.Inclinacion,
    PotenciaMotor: escalera.PotenciaMotor,
    Especificaciones: escalera.Especificaciones
});

interface CrearCotizacionNBSCVProps {
    alTerminar?: () => void;
    cotizacionInicialId?: string;
}

export const CrearCotizacionNBSCV: React.FC<CrearCotizacionNBSCVProps> = ({ alTerminar, cotizacionInicialId }) => {
    const { usuario } = useAutenticacion();
    const { mostrarNotificacion } = useNotificaciones();
    const avisarErrorConsulta = useAvisoErrorConsulta();
    const { manejarCargando } = useCargando();
    const { confirmar, confirmarConCierre } = useConfirmacion();
    const pdf = usePdfCotizacion();
    const [cotizacion, setCotizacion] = useState<ICotizacionNBS>(estadoInicialCotizacionNBS);
    const [ciudades, setCiudades] = useState<{value: string, label: string}[]>([]);
    const [cargandoInicial, setCargandoInicial] = useState<boolean>(!!cotizacionInicialId);
    const [pasoInicial, setPasoInicial] = useState<number>(0);
    // Hay cambios en el proyecto o en los equipos que el Resumen todavía no refleja (hay que recalcular).
    const [cambiosSinCalcular, setCambiosSinCalcular] = useState(false);
    // Variación mensual del flete del país (informativa en el Resumen).
    const [variacionMensual, setVariacionMensual] = useState<IVariacionMensualMes[]>([]);
    const paisCotizacion = cotizacion.PaisId ?? usuario?.PaisId;

    React.useEffect(() => {
        if (!paisCotizacion) return;
        let vigente = true;
        new CotizadorModelo().obtenerMatrices<IVariacionMensualMes>('variacion-mensual', paisCotizacion)
            .then(respuesta => {
                if (vigente) setVariacionMensual((respuesta?.Data ?? []).map(v => ({ MesNumero: Number(v.MesNumero), Porcentaje: Number(v.Porcentaje) })));
            })
            .catch(error => avisarErrorConsulta(error, 'la variación mensual del flete'));
        return () => { vigente = false; };
    }, [paisCotizacion, avisarErrorConsulta]);

    React.useEffect(() => {
        const fetchCiudades = async () => {
            try {
                const modelo = new UbicacionModelo();
                const respuesta = await modelo.obtenerCiudades();
                if (respuesta && respuesta.Data) {
                    const ciudadesFiltradas = respuesta.Data.filter(c => c.PaisId === (usuario?.PaisId || 1));
                    setCiudades(ciudadesFiltradas.map(c => ({ value: c.Nombre, label: c.Nombre })));
                }
            } catch (error) {
                avisarErrorConsulta(error, 'las ciudades');
            }
        };
        fetchCiudades();
    }, [usuario?.PaisId]);

    const cargarCotizacionDesdeBackend = async (id: string, mantenerPaso: boolean = false) => {
        try {
            const modelo = new CotizadorModelo();
            const respuesta = await modelo.obtenerCotizacion(id);
            if (respuesta && respuesta.Data) {
                const dataBackend = respuesta.Data;
                const equiposAscensores = dataBackend.Equipos?.filter((e: any) => e.TipoEquipo === 0) || [];
                const equiposEscaleras = dataBackend.Equipos?.filter((e: any) => e.TipoEquipo === 1) || [];

                const cotizacionMapeada: ICotizacionNBS = {
                    Id: dataBackend.Id,
                    FechaCreacion: dataBackend.FechaCreacion,
                    PaisId: dataBackend.PaisId,
                    MonedaLocal: dataBackend.Pais?.Moneda?.trim() || 'COP',
                    UsaTrm: monedaUsaTrm(dataBackend.Pais?.Moneda),
                    Estado: {
                        Id: dataBackend.Estado,
                        Nombre: nombreEstadoCotizacion(dataBackend.Estado)
                    },
                    Codigo: dataBackend.Codigo,
                    Proyecto: {
                        NombreCliente: dataBackend.NombreCliente || '',
                        Nombre: dataBackend.NombreProyecto || '',
                        Ciudad: dataBackend.Ciudad || '',
                        OtrasCiudades: false,
                        Anio: dataBackend.Anio || new Date().getFullYear(),
                        MesesContrato: dataBackend.MesesContrato || 0,
                        MesesMantenimiento: dataBackend.MesesMantenimiento || 0,
                        AplicaPolizas: dataBackend.AplicaPolizas ?? true,
                        PlazoEntregaMeses: dataBackend.PlazoEntregaMeses ?? null,
                        MesesInstalacion: dataBackend.MesesInstalacion ?? null,
                        GarantiaFabricacionMeses: dataBackend.GarantiaFabricacionMeses ?? null,
                        GarantiaInstalacionMeses: dataBackend.GarantiaInstalacionMeses ?? null,
                        Trm: dataBackend.ConfiguracionGlobal?.Trm || 0,
                    },
                    Ascensores: equiposAscensores.map((e: any) => ({
                        Id: e.Id,
                        Nombre: e.Nombre || '',
                        Tipo: e.AscensorDetalle?.Tipo || '',
                        Uso: e.AscensorDetalle?.Uso || '',
                        Producto: String(e.ProductoId ?? ''),
                        ProductoNombre: e.Producto?.Nombre,
                        Capacidad: e.AscensorDetalle?.Capacidad || 0,
                        Pasajeros: e.AscensorDetalle?.Pasajeros ?? null,
                        Especificaciones: especificacionesAscensorDesdeApi(e.AscensorDetalle?.Especificaciones),
                        VelocidadIngreso: e.AscensorDetalle?.VelocidadIngreso || 0,
                        OperadorPuerta: String(e.AscensorDetalle?.OperadorPuertaId ?? ''),
                        OperadorPuertaNombre: e.AscensorDetalle?.OperadorPuerta?.Nombre,
                        Paradas: e.AscensorDetalle?.Paradas || 0,
                        Maquina: String(e.AscensorDetalle?.MaquinaId ?? ''),
                        MaquinaNombre: e.AscensorDetalle?.Maquina?.Nombre,
                        PotenciaMotor: e.AscensorDetalle?.PotenciaMotor || 0,
                        Cantidad: e.Cantidad || 0,
                        CostoFCA: e.CostoFCA || 0,
                        OtrasCiudades: e.OtrasCiudades || false,
                        DiasViaticos: e.DiasInstalacion || 0,
                        ValorViaticos: e.Viaticos || 0,
                        ValorDesplazamientos: e.Desplazamientos || 0,
                        ValorVuelos: e.Vuelos || 0,
                        resultados: mapearResultadosEquipo(e)
                    })),
                    Escaleras: equiposEscaleras.map((e: any) => ({
                        Id: e.Id,
                        Nombre: e.Nombre || '',
                        Tipo: e.EscaleraDetalle?.Tipo || '',
                        Producto: String(e.ProductoId ?? ''),
                        ProductoNombre: e.Producto?.Nombre,
                        Contenedores: e.EscaleraDetalle?.Contenedores || 0,
                        Altura: e.EscaleraDetalle?.Altura || 0,
                        VelocidadIngreso: e.EscaleraDetalle?.VelocidadIngreso || 0,
                        AnchoPasos: e.EscaleraDetalle?.AnchoPasos || 0,
                        Inclinacion: e.EscaleraDetalle?.Inclinacion || 0,
                        PotenciaMotor: e.EscaleraDetalle?.PotenciaMotor || 0,
                        Especificaciones: especificacionesEscaleraDesdeApi(e.EscaleraDetalle?.Especificaciones),
                        Cantidad: e.Cantidad || 0,
                        CostoFCA: e.CostoFCA || 0,
                        OtrasCiudades: e.OtrasCiudades || false,
                        DiasViaticos: e.DiasInstalacion || 0,
                        ValorViaticos: e.Viaticos || 0,
                        ValorDesplazamientos: e.Desplazamientos || 0,
                        ValorVuelos: e.Vuelos || 0,
                        resultados: mapearResultadosEquipo(e)
                    })),
                    ResultadosGlobales: {
                        PrecioVentaTotal: dataBackend.PrecioVentaTotal ?? 0,
                        CostoImportadoTotal: dataBackend.CostoImportadoTotal ?? 0,
                        CostoLocalTotal: dataBackend.CostoLocalTotal ?? 0,
                        ComisionTotal: dataBackend.ComisionTotal ?? 0,
                        MargenPromedioPorcentaje: dataBackend.MargenPromedioPorcentaje ?? 0,
                        MargenImportadoVentaAscensores: dataBackend.MargenImportadoVentaAscensores,
                        MargenLocalVentaAscensores: dataBackend.MargenLocalVentaAscensores,
                        MargenPromedioAscensores: dataBackend.MargenPromedioAscensores,
                        MargenImportadoVentaEscaleras: dataBackend.MargenImportadoVentaEscaleras,
                        MargenLocalVentaEscaleras: dataBackend.MargenLocalVentaEscaleras,
                        MargenPromedioEscaleras: dataBackend.MargenPromedioEscaleras,
                        ComisionPorcentajeAscensores: dataBackend.ComisionPorcentajeAscensores,
                        ComisionPorcentajeEscaleras: dataBackend.ComisionPorcentajeEscaleras,
                        ComisionAscensores: dataBackend.ComisionAscensores,
                        ComisionEscaleras: dataBackend.ComisionEscaleras
                    },
                    // Si la cotización no lo ha sobrescrito (null), se muestra el de la configuración global.
                    // Nunca caer a 0: el PUT lo guardaría como valor propio de la cotización y el motor lo usaría.
                    MargenImportadoAscensores: dataBackend.MargenImportadoAscensores ?? dataBackend.ConfiguracionGlobal?.MargenImportadoAscensor,
                    MargenLocalAscensores: dataBackend.MargenLocalAscensores ?? dataBackend.ConfiguracionGlobal?.MargenLocalAscensor,
                    MargenImportadoEscaleras: dataBackend.MargenImportadoEscaleras ?? dataBackend.ConfiguracionGlobal?.MargenImportadoEscalera,
                    MargenLocalEscaleras: dataBackend.MargenLocalEscaleras ?? dataBackend.ConfiguracionGlobal?.MargenLocalEscalera,
                    DescuentoAscensores: dataBackend.DescuentoAscensores ?? dataBackend.ConfiguracionGlobal?.DescuentoAscensor,
                    DescuentoEscaleras: dataBackend.DescuentoEscaleras ?? dataBackend.ConfiguracionGlobal?.DescuentoEscalera,
                    FleteMaritimoAscensores: dataBackend.FleteMaritimoAscensores ?? dataBackend.ConfiguracionGlobal?.FleteMaritimoAscensor ?? 0,
                    FleteMaritimoEscaleras: dataBackend.FleteMaritimoEscaleras ?? dataBackend.ConfiguracionGlobal?.FleteMaritimoEscalera ?? 0,
                    PermiteEdicion: dataBackend.PermiteEdicion,
                    TransicionesPermitidas: dataBackend.TransicionesPermitidas ?? [],
                    ConfiguracionDesactualizada: dataBackend.ConfiguracionDesactualizada ?? false,
                    MatricesDesactualizadas: dataBackend.MatricesDesactualizadas ?? [],
                    CalculoCompleto: dataBackend.CalculoCompleto ?? false,
                    RequiereRecalculo: dataBackend.RequiereRecalculo ?? false,
                    ObservacionesCalculo: dataBackend.ObservacionesCalculo ? JSON.parse(dataBackend.ObservacionesCalculo) : [],
                    ConfiguracionUsada: dataBackend.ConfiguracionGlobal,
                    ConfiguracionPersonalizada: dataBackend.ConfiguracionPersonalizada ?? false
                };

                setCotizacion(cotizacionMapeada);
                if (!mantenerPaso) {
                    // Ya generada (cualquier estado distinto de Borrador): se abre en el Resumen, sin recalcular.
                    // Borrador: en Equipos si ya tiene, si no en Proyecto.
                    if (cotizacionMapeada.Estado?.Id !== EnumEstadoCotizacionNBS.Borrador) {
                        setPasoInicial(2);
                    } else if (cotizacionMapeada.Ascensores.length > 0 || cotizacionMapeada.Escaleras.length > 0) {
                        setPasoInicial(1);
                    }
                }
                return cotizacionMapeada;
            }
        } catch (error) {
            console.error("Error al cargar cotizacion inicial", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", "No se pudo cargar la cotización.");
        }
        return null;
    };

    React.useEffect(() => {
        if (cotizacionInicialId) {
            const initFetch = async () => {
                manejarCargando(true, "Cargando cotización...", false);
                // No se recalcula al abrir: si la configuración global cambió, el Resumen muestra el aviso
                // y el usuario decide con "Usar configuración global".
                await cargarCotizacionDesdeBackend(cotizacionInicialId);
                setCargandoInicial(false);
                manejarCargando(false);
            };
            initFetch();
        }
    }, [cotizacionInicialId]);

    if (cargandoInicial) {
        return null;
    }

    // Solo Aceptada es de solo lectura (el back lo valida). Cotización nueva: editable.
    const esSoloLectura = cotizacion.PermiteEdicion === false;

    const alCambiarProyecto = (campo: keyof ICotizacionNBS['Proyecto'], valor: any) => {
        setCambiosSinCalcular(true);
        setCotizacion(prev => ({
            ...prev,
            Proyecto: {
                ...prev.Proyecto,
                [campo]: valor
            }
        }));
    };


    /** Datos del proyecto para el PUT. Márgenes, descuentos y flete se reenvían tal como vienen del back
     * (el PUT los sobrescribe); solo se editan en la pestaña Configuración del Resumen. */
    const construirDatosCotizacion = () => ({
        UsuarioCreadorId: usuario?.Id || 1,
        PaisId: usuario?.PaisId || 1,
        NombreCliente: cotizacion.Proyecto.NombreCliente.trim(),
        NombreProyecto: cotizacion.Proyecto.Nombre,
        Ciudad: cotizacion.Proyecto.Ciudad,
        Anio: cotizacion.Proyecto.Anio,
        MesesContrato: cotizacion.Proyecto.MesesContrato,
        MesesMantenimiento: cotizacion.Proyecto.MesesMantenimiento,
        AplicaPolizas: cotizacion.Proyecto.AplicaPolizas,
        PlazoEntregaMeses: cotizacion.Proyecto.PlazoEntregaMeses,
        MesesInstalacion: cotizacion.Proyecto.MesesInstalacion,
        GarantiaFabricacionMeses: cotizacion.Proyecto.GarantiaFabricacionMeses,
        GarantiaInstalacionMeses: cotizacion.Proyecto.GarantiaInstalacionMeses,
        MargenImportadoAscensores: cotizacion.MargenImportadoAscensores,
        MargenLocalAscensores: cotizacion.MargenLocalAscensores,
        MargenImportadoEscaleras: cotizacion.MargenImportadoEscaleras,
        MargenLocalEscaleras: cotizacion.MargenLocalEscaleras,
        DescuentoAscensores: cotizacion.DescuentoAscensores,
        DescuentoEscaleras: cotizacion.DescuentoEscaleras,
        FleteMaritimoAscensores: cotizacion.FleteMaritimoAscensores,
        FleteMaritimoEscaleras: cotizacion.FleteMaritimoEscaleras
    });

    const avanzarPaso1 = async () => {
        // Solo lectura (Aceptada): no se envían cambios (el back rechaza el PUT).
        if (esSoloLectura) return;
        try {
            const modelo = new CotizadorModelo();
            const idCotizacion = cotizacion.Id;
            const datosCotizacion = construirDatosCotizacion();

            if (idCotizacion) {
                await modelo.actualizarCotizacion(idCotizacion as string, datosCotizacion);
            } else {
                const respuestaPaso1 = await modelo.crearCotizacionPaso1(datosCotizacion as any);
                const cotizacionId = (respuestaPaso1 as any)?.data || (respuestaPaso1 as any)?.Data || respuestaPaso1;
                
                // Cargar datos reales desde el backend para poblar los márgenes/descuentos por defecto
                await cargarCotizacionDesdeBackend(cotizacionId, true);
            }
        } catch (error: any) {
            console.error("Error al guardar el Proyecto", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "Error al guardar la cotización en el backend.");
            throw error;
        }
    };

    function mapearResultadosEquipo(equipoBackend: any) { return {
        CantidadContenedoresCalculada: equipoBackend.CantidadContenedoresCalculada ?? 0,
        CantidadContenedoresManual: equipoBackend.CantidadContenedoresManual ?? null,
        ValorDescuentoKone: equipoBackend.ValorDescuentoKone ?? 0,
        CostoFCAConDescuentoKone: equipoBackend.CostoFCAConDescuentoKone ?? 0,
        Sia: equipoBackend.Sia ?? 0,
        Seguro: equipoBackend.Seguro ?? 0,
        Montacargas: equipoBackend.Montacargas ?? 0,
        FleteNacional: equipoBackend.FleteNacional ?? 0,
        SubTotalOtrosImportados: equipoBackend.SubTotalOtrosImportados ?? 0,
        SubTotalImportados: equipoBackend.SubTotalImportados ?? 0,
        TotalImportadoSinDescuento: equipoBackend.TotalImportadoSinDescuento ?? 0,
        Comision: equipoBackend.Comision ?? 0,
        Upme: equipoBackend.Upme ?? 0,
        Polizas: equipoBackend.Polizas ?? 0,
        TotalImportado: equipoBackend.TotalImportado ?? 0,
        FobUnitario: equipoBackend.FobUnitario ?? 0,
        FleteUnitarioXContainer: equipoBackend.FleteUnitarioXContainer ?? 0,
        ValorFleteUnitario: equipoBackend.ValorFleteUnitario ?? 0,
        TotalComponenteImportado: equipoBackend.TotalComponenteImportado ?? 0,
        DiasInstalacion: equipoBackend.DiasInstalacion ?? 0,
        Moi: equipoBackend.Moi ?? 0,
        Fic: equipoBackend.Fic ?? 0,
        Cif: equipoBackend.Cif ?? 0,
        SubTotalManoObra: equipoBackend.SubTotalManoObra ?? 0,
        BateriasEbd: equipoBackend.BateriasEbdOHerramientas ?? 0,
        Transformador: equipoBackend.Transformador ?? 0,
        Desembalaje: equipoBackend.Desembalaje ?? 0,
        MontacargasTrasiegos: equipoBackend.MontacargasTrasiegos ?? 0,
        InsumosInstalacion: equipoBackend.InsumosInstalacion ?? 0,
        CerramientoAlmacen: 0,
        CertificacionNTC: equipoBackend.CertificacionNTC ?? 0,
        Imprevistos: equipoBackend.Imprevistos ?? 0,
        TotalLocalUSD: 0,
        TotalLocalSinDescuento: equipoBackend.TotalLocalSinDescuento ?? 0,
        Viaticos: equipoBackend.Viaticos ?? 0,
        Desplazamientos: equipoBackend.Desplazamientos ?? 0,
        Vuelos: equipoBackend.Vuelos ?? 0,
        MttoFreeOfrecido: equipoBackend.MttoFreeOfrecido ?? 0,
        ComponenteSinInstalacion: equipoBackend.ComponenteSinInstalacion ?? 0,
        TotalLocal: equipoBackend.TotalLocal ?? 0,
        TotalLocalConvertido: equipoBackend.TotalLocalConvertido ?? 0,
        ComisionComercial: equipoBackend.ComisionComercial ?? 0,
        MargenImportadoPorcentaje: equipoBackend.MargenImportadoPorcentaje ?? 0,
        MargenImportadoPesos: equipoBackend.MargenImportadoPesos ?? 0,
        MargenLocalPorcentaje: equipoBackend.MargenLocalPorcentaje ?? 0,
        MargenLocalPesos: equipoBackend.MargenLocalPesos ?? 0,
        PrecioVentaSinImpuestos: equipoBackend.PrecioVentaSinImpuestos ?? 0
    }; }

    const avanzarPaso2 = async () => {
        const cotizacionId = cotizacion.Id;
        // Ya calculada = salió de Borrador. Borrador siempre se calcula al llegar al Resumen.
        const yaCalculada = cotizacion.Estado?.Id !== EnumEstadoCotizacionNBS.Borrador;
        const hayCambios = cambiosSinCalcular || cotizacion.RequiereRecalculo === true;
        if (cotizacionId && !esSoloLectura && yaCalculada && hayCambios) {
            // El recálculo es necesario (cambió un equipo o el proyecto), pero usa las matrices actuales:
            // si cambiaron después del último cálculo, se avisa que afecta a toda la cotización.
            const matrices = cotizacion.MatricesDesactualizadas ?? [];
            if (matrices.length > 0) {
                const continuar = await confirmar({
                    titulo: 'Matrices actualizadas',
                    mensaje: `${matrices.join(', ')} ${matrices.length === 1 ? 'cambió' : 'cambiaron'} después del último cálculo. Al continuar, toda la cotización se recalculará con las matrices actuales, no solo los equipos que modificaste. ¿Deseas continuar?`,
                    tipo: EnumTipoNotificacion.Warning,
                    textoAceptar: 'Continuar',
                    textoCancelar: 'Cancelar',
                    opcionCancelar: true
                });
                // Sin error ni notificación: el paso no avanza y el usuario sigue en Equipos.
                if (!continuar) throw new Error('Recálculo cancelado por el usuario');
            }
        }
        try {
            if (cotizacionId && esSoloLectura) {
                // Aceptada no se recalcula (el back lo rechaza): solo se consulta.
                await cargarCotizacionDesdeBackend(cotizacionId, true);
                return;
            }
            if (cotizacionId && yaCalculada && !hayCambios) {
                // Solo se revisaron los equipos: se vuelve al Resumen con el cálculo guardado, sin recalcular.
                await cargarCotizacionDesdeBackend(cotizacionId, true);
                return;
            }
            if (cotizacionId) {
                const modelo = new CotizadorModelo();
                // Llegar al Resumen genera la cotización: si queda completa y estaba en Borrador,
                // el back la pasa a Revisión Interna. En otros estados solo recalcula.
                const generarRespuesta = await modelo.generarCotizacion(cotizacionId);

                const dataGenerar = generarRespuesta.Data;
                if (dataGenerar && dataGenerar.EstaCompleto === false) {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Warning,
                        "Resumen parcial",
                        "Faltan datos para completar el cálculo. Revisa las observaciones en el Resumen; los valores que no se pudieron calcular aparecen vacíos."
                    );
                }

                // Refrescar cotizacion completa para traer todos los cálculos y defaults
                await cargarCotizacionDesdeBackend(cotizacionId, true);
                setCambiosSinCalcular(false);
            }
        } catch (error: any) {
            console.error("Error al calcular resultados", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "Error al calcular totales de la cotización.");
            throw error;
        }
    };

    const autoguardarCotizacion = () => {
        // Llamado a la API para autoguardar sin terminar
    };

    /**
     * Botón "Generar PDF". No recalcula en silencio: si las matrices cambiaron después del último cálculo,
     * el usuario elige recalcular antes o generar con el cálculo actual. Solo con el cálculo completo.
     * En Aceptada no se recalcula: el PDF sale con los valores congelados de la cotización.
     */
    const generarPdf = async () => {
        if (!cotizacion.Id) return;
        if (esSoloLectura) {
            await continuarPdf(false);
            return;
        }
        const matrices = cotizacion.MatricesDesactualizadas ?? [];
        if (matrices.length > 0) {
            // Cerrar con la X (null) aborta; los botones deciden si recalcular antes.
            const recalcular = await confirmarConCierre({
                titulo: "Matrices actualizadas",
                mensaje: `Después del último cálculo se actualizaron: ${matrices.join(', ')}. ¿Deseas recalcular la cotización antes de generar el PDF?`,
                tipo: EnumTipoNotificacion.Warning,
                textoAceptar: "Recalcular y generar",
                textoCancelar: "Generar con el cálculo actual",
                opcionCancelar: true
            });
            if (recalcular === null) return;
            await continuarPdf(recalcular);
            return;
        }
        await continuarPdf(false);
    };

    /** Abre la previsualización del PDF (desde ahí se descarga), recalculando antes si el usuario lo eligió
     *  en el modal de matrices. */
    const continuarPdf = async (recalcular: boolean) => {
        if (!cotizacion.Id) return;
        if (recalcular) {
            try {
                manejarCargando(true, "Recalculando cotización...", false);
                const resultado = (await new CotizadorModelo().generarCotizacion(cotizacion.Id)).Data;
                await cargarCotizacionDesdeBackend(cotizacion.Id, true);
                if (resultado?.EstaCompleto === false) {
                    mostrarNotificacion(EnumTipoNotificacion.Warning, "Cotización incompleta",
                        "Con las matrices actuales faltan datos: revisa las observaciones del Resumen antes de generar el PDF.");
                    return;
                }
            } catch (error: any) {
                console.error("Error al recalcular antes del PDF", error);
                mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudo recalcular la cotización.");
                return;
            } finally {
                manejarCargando(false);
            }
        }
        await pdf.previsualizar(cotizacion.Id);
    };

    /**
     * Contenedores de un ascensor corregidos a mano desde el detalle (null = volver al calculado).
     * El back guarda los contenedores y recalcula toda la cotización. Devuelve true si quedó guardado.
     */
    const actualizarContenedores = async (equipoId: string, cantidadContenedores: number | null): Promise<boolean> => {
        if (!cotizacion.Id || esSoloLectura) return false;
        try {
            manejarCargando(true, "Recalculando cotización...", false);
            await new CotizadorModelo().actualizarContenedoresEquipo(cotizacion.Id, equipoId, cantidadContenedores);
            await cargarCotizacionDesdeBackend(cotizacion.Id, true);
            mostrarNotificacion(
                EnumTipoNotificacion.Success,
                "Contenedores",
                cantidadContenedores === null
                    ? "Se restauraron los contenedores calculados y se recalculó la cotización."
                    : "Contenedores actualizados y cotización recalculada."
            );
            return true;
        } catch (error: any) {
            console.error("Error al actualizar contenedores", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudieron actualizar los contenedores.");
            return false;
        } finally {
            manejarCargando(false);
        }
    };

    /** Guarda variables de configuración solo para esta cotización y recarga el cálculo. true si quedó guardado. */
    const guardarConfiguracionCotizacion = async (configuracion: IConfiguracionState): Promise<boolean> => {
        if (!cotizacion.Id || esSoloLectura) return false;
        try {
            manejarCargando(true, "Recalculando cotización...", false);
            await new CotizadorModelo().guardarConfiguracionCotizacion(cotizacion.Id, configuracion);
            await cargarCotizacionDesdeBackend(cotizacion.Id, true);
            mostrarNotificacion(EnumTipoNotificacion.Success, "Configuración de la cotización", "Los cambios aplican solo a esta cotización; la configuración global no cambió.");
            return true;
        } catch (error: any) {
            console.error("Error al guardar la configuración de la cotización", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudo guardar la configuración de la cotización.");
            return false;
        } finally {
            manejarCargando(false);
        }
    };

    /** Recalcula la cotización (avisos de matrices modificadas o de cambios sin calcular). */
    const recalcularConMatricesVigentes = async () => {
        if (!cotizacion.Id || esSoloLectura) return;
        try {
            manejarCargando(true, "Recalculando cotización...", false);
            await new CotizadorModelo().generarCotizacion(cotizacion.Id);
            await cargarCotizacionDesdeBackend(cotizacion.Id, true);
            mostrarNotificacion(EnumTipoNotificacion.Success, "Cotización recalculada", "La cotización se recalculó con los datos y matrices actuales.");
        } catch (error: any) {
            console.error("Error al recalcular la cotización", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudo recalcular la cotización.");
        } finally {
            manejarCargando(false);
        }
    };

    /** Vuelve a la configuración global vigente (descarta la propia) y recalcula. */
    const usarConfiguracionGlobal = async () => {
        if (!cotizacion.Id || esSoloLectura) return;
        const confirmado = await confirmar({
            titulo: 'Usar configuración global',
            mensaje: 'La cotización se recalculará con la configuración global vigente. Se descartan los cambios hechos solo para esta cotización, incluidos márgenes, descuentos y flete marítimo. ¿Deseas continuar?',
            tipo: EnumTipoNotificacion.Warning,
            textoAceptar: 'Usar configuración global',
            textoCancelar: 'Cancelar'
        });
        if (!confirmado) return;

        try {
            manejarCargando(true, "Recalculando cotización...", false);
            await new CotizadorModelo().usarConfiguracionGlobal(cotizacion.Id);
            await cargarCotizacionDesdeBackend(cotizacion.Id, true);
            mostrarNotificacion(EnumTipoNotificacion.Success, "Configuración global", "La cotización se recalculó con la configuración global vigente.");
        } catch (error: any) {
            console.error("Error al usar la configuración global", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudo aplicar la configuración global.");
        } finally {
            manejarCargando(false);
        }
    };

    /** Cambio manual de estado (Revisión Interna/Cliente, Rechazada, Aceptada). El back valida la transición. */
    const cambiarEstado = async (estado: EstadoCotizacionNBSId) => {
        if (!cotizacion.Id) return;
        const accion = accionTransicionEstado(cotizacion.Estado?.Id as number, estado);
        const confirmado = await confirmar({
            titulo: accion.etiqueta,
            mensaje: accion.confirmacion,
            tipo: EnumTipoNotificacion.Warning,
            textoAceptar: accion.etiqueta,
            textoCancelar: 'Cancelar'
        });
        if (!confirmado) return;

        try {
            manejarCargando(true, "Actualizando estado...", false);
            await new CotizadorModelo().cambiarEstadoCotizacion(cotizacion.Id, estado);
            await cargarCotizacionDesdeBackend(cotizacion.Id, true);
            mostrarNotificacion(EnumTipoNotificacion.Success, "Estado actualizado", `La cotización está en ${nombreEstadoCotizacion(estado)}.`);
        } catch (error: any) {
            console.error("Error al cambiar el estado", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudo cambiar el estado de la cotización.");
        } finally {
            manejarCargando(false);
        }
    };

    const cancelar = () => {
        if (alTerminar) alTerminar();
    };

    const agregarAscensor = async (ascensor: IEquipoAscensor) => {
        try {
            let resultadoGuardado = null;
            let response: any = null;
            if (cotizacion.Id) {
                const modelo = new CotizadorModelo();
                response = await modelo.agregarEquipo(cotizacion.Id, {
                    TipoEquipo: 0, // 0 = Ascensor
                    Nombre: ascensor.Nombre,
                    ProductoId: Number(ascensor.Producto),
                    Cantidad: ascensor.Cantidad,
                    CostoFCA: ascensor.CostoFCA,
                    OtrasCiudades: ascensor.OtrasCiudades || false,
                    Ascensor: detalleAscensorApi(ascensor)
                });
                resultadoGuardado = response.data || response.Data || response;
                
                
                // Actualizar el ascensor con el Id real devuelto por la API
                const eqId = resultadoGuardado?.equipoId || resultadoGuardado?.EquipoId;
                if (eqId) {
                    ascensor.Id = eqId;
                    ascensor.DiasViaticos = resultadoGuardado.diasInstalacion || resultadoGuardado.DiasInstalacion;
                    ascensor.ValorViaticos = resultadoGuardado.viaticos || resultadoGuardado.Viaticos;
                    ascensor.ValorDesplazamientos = resultadoGuardado.desplazamientos || resultadoGuardado.Desplazamientos;
                    ascensor.ValorVuelos = resultadoGuardado.vuelos ?? resultadoGuardado.Vuelos;
                }

                const notifType = response.NotificationType ?? response.notificationType;
                const msg = response.Message || response.message || response.Messages?.[0] || response.messages?.[0] || "Equipo agregado exitosamente";

                if (notifType === 1) {
                    mostrarNotificacion(EnumTipoNotificacion.Success, "Éxito", msg);
                }
            }
            setCambiosSinCalcular(true);
            setCotizacion(prev => ({
                ...prev,
                Ascensores: [...prev.Ascensores, ascensor]
            }));
            return response;
        } catch (error: any) {
            console.error("Error al guardar ascensor", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error al guardar", error?.mensajeBackend || "No se pudo guardar el equipo. Por favor, intenta de nuevo.");
            throw error;
        }
    };

    const agregarEscalera = async (escalera: IEquipoEscalera) => {
        try {
            let resultadoGuardado = null;
            let response: any = null;
            if (cotizacion.Id) {
                const modelo = new CotizadorModelo();
                response = await modelo.agregarEquipo(cotizacion.Id, {
                    TipoEquipo: 1, // 1 = Escalera
                    Nombre: escalera.Nombre,
                    ProductoId: Number(escalera.Producto),
                    Cantidad: escalera.Cantidad,
                    CostoFCA: escalera.CostoFCA,
                    OtrasCiudades: escalera.OtrasCiudades || false,
                    Escalera: detalleEscaleraApi(escalera)
                });
                resultadoGuardado = response.data || response.Data || response;

                const eqId = resultadoGuardado?.equipoId || resultadoGuardado?.EquipoId;
                if (eqId) {
                    escalera.Id = eqId;
                    escalera.DiasViaticos = resultadoGuardado.diasInstalacion || resultadoGuardado.DiasInstalacion;
                    escalera.ValorViaticos = resultadoGuardado.viaticos || resultadoGuardado.Viaticos;
                    escalera.ValorDesplazamientos = resultadoGuardado.desplazamientos || resultadoGuardado.Desplazamientos;
                    escalera.ValorVuelos = resultadoGuardado.vuelos ?? resultadoGuardado.Vuelos;
                }

                const notifType = response.NotificationType ?? response.notificationType;
                const msg = response.Message || response.message || response.Messages?.[0] || response.messages?.[0] || "Equipo agregado exitosamente";

                if (notifType === 1) {
                    mostrarNotificacion(EnumTipoNotificacion.Success, "Éxito", msg);
                }
            }
            setCambiosSinCalcular(true);
            setCotizacion(prev => ({
                ...prev,
                Escaleras: [...prev.Escaleras, escalera]
            }));
            return response;
        } catch (error: any) {
            console.error("Error al guardar escalera", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error al guardar", error?.mensajeBackend || "No se pudo guardar el equipo. Por favor, intenta de nuevo.");
            throw error;
        }
    };

    const editarAscensor = async (ascensor: IEquipoAscensor) => {
        try {
            let resultadoEditado = null;
            let response: any = null;
            if (cotizacion.Id && ascensor.Id) {
                const modelo = new CotizadorModelo();
                response = await modelo.editarEquipo(cotizacion.Id, ascensor.Id, {
                    TipoEquipo: 0,
                    Nombre: ascensor.Nombre,
                    ProductoId: Number(ascensor.Producto),
                    Cantidad: ascensor.Cantidad,
                    CostoFCA: ascensor.CostoFCA,
                    OtrasCiudades: ascensor.OtrasCiudades || false,
                    Ascensor: detalleAscensorApi(ascensor)
                });
                resultadoEditado = response.data || response.Data || response;
                
                const eqId = resultadoEditado?.equipoId || resultadoEditado?.EquipoId;
                if (eqId) {
                    ascensor.DiasViaticos = resultadoEditado.diasInstalacion || resultadoEditado.DiasInstalacion;
                    ascensor.ValorViaticos = resultadoEditado.viaticos || resultadoEditado.Viaticos;
                    ascensor.ValorDesplazamientos = resultadoEditado.desplazamientos || resultadoEditado.Desplazamientos;
                    ascensor.ValorVuelos = resultadoEditado.vuelos ?? resultadoEditado.Vuelos;
                }

                const notifType = response.NotificationType ?? response.notificationType;
                const msg = response.Message || response.message || response.Messages?.[0] || response.messages?.[0] || "Equipo actualizado exitosamente";

                if (notifType === 1) {
                    mostrarNotificacion(EnumTipoNotificacion.Success, "Éxito", msg);
                }
            }
            setCambiosSinCalcular(true);
            setCotizacion(prev => ({
                ...prev,
                Ascensores: prev.Ascensores.map(a => a.Id === ascensor.Id ? { ...a, ...ascensor } : a)
            }));
            return response;
        } catch (error: any) {
            console.error("Error al editar ascensor", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error al actualizar", error?.mensajeBackend || "No se pudo actualizar el equipo.");
            throw error;
        }
    };

    const editarEscalera = async (escalera: IEquipoEscalera) => {
        try {
            let resultadoEditado = null;
            let response: any = null;
            if (cotizacion.Id && escalera.Id) {
                const modelo = new CotizadorModelo();
                response = await modelo.editarEquipo(cotizacion.Id, escalera.Id, {
                    TipoEquipo: 1,
                    Nombre: escalera.Nombre,
                    ProductoId: Number(escalera.Producto),
                    Cantidad: escalera.Cantidad,
                    CostoFCA: escalera.CostoFCA,
                    OtrasCiudades: escalera.OtrasCiudades || false,
                    Escalera: detalleEscaleraApi(escalera)
                });
                resultadoEditado = response.data || response.Data || response;

                const eqId = resultadoEditado?.equipoId || resultadoEditado?.EquipoId;
                if (eqId) {
                    escalera.DiasViaticos = resultadoEditado.diasInstalacion || resultadoEditado.DiasInstalacion;
                    escalera.ValorViaticos = resultadoEditado.viaticos || resultadoEditado.ValorViaticos;
                    escalera.ValorDesplazamientos = resultadoEditado.desplazamientos || resultadoEditado.Desplazamientos;
                    escalera.ValorVuelos = resultadoEditado.vuelos ?? resultadoEditado.Vuelos;
                }

                const notifType = response.NotificationType ?? response.notificationType;
                const msg = response.Message || response.message || response.Messages?.[0] || response.messages?.[0] || "Equipo actualizado exitosamente";

                if (notifType === 1) {
                    mostrarNotificacion(EnumTipoNotificacion.Success, "Éxito", msg);
                }
            }
            setCambiosSinCalcular(true);
            setCotizacion(prev => ({
                ...prev,
                Escaleras: prev.Escaleras.map(a => a.Id === escalera.Id ? { ...a, ...escalera } : a)
            }));
            return response;
        } catch (error: any) {
            console.error("Error al editar escalera", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error al actualizar", error?.mensajeBackend || "No se pudo actualizar el equipo.");
            throw error;
        }
    };

    const eliminarEquipo = async (Id: string, Tipo: 'ascensor' | 'escalera') => {
        try {
            const idCotizacion = cotizacion.Id;
            if (idCotizacion) {
                const modelo = new CotizadorModelo();
                await modelo.eliminarEquipo(idCotizacion, Id);
                mostrarNotificacion(EnumTipoNotificacion.Success, "Éxito", "Equipo eliminado correctamente");
            }
            setCambiosSinCalcular(true);
            setCotizacion(prev => ({
                ...prev,
                Ascensores: Tipo === 'ascensor' ? prev.Ascensores.filter(e => e.Id !== Id) : prev.Ascensores,
                Escaleras: Tipo === 'escalera' ? prev.Escaleras.filter(e => e.Id !== Id) : prev.Escaleras,
            }));
        } catch (error: any) {
            console.error("Error al eliminar equipo", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "Error", error?.mensajeBackend || "No se pudo eliminar el equipo");
        }
    };

    return (
        <>
        <CrearCotizacionNBSVista
            cotizacion={cotizacion}
            pasoInicial={pasoInicial}
            esPanama={paisCotizacion === PAIS_PANAMA_ID}
            alCambiarProyecto={alCambiarProyecto}
            alGuardar={generarPdf}
            alCambiarEstado={cambiarEstado}
            alAutoguardar={autoguardarCotizacion}
            alAvanzarPaso1={avanzarPaso1}
            alAvanzarPaso2={avanzarPaso2}
            alCancelar={cancelar}
            agregarAscensor={agregarAscensor}
            agregarEscalera={agregarEscalera}
            editarAscensor={editarAscensor}
            editarEscalera={editarEscalera}
            eliminarEquipo={eliminarEquipo}
            alActualizarContenedores={actualizarContenedores}
            alGuardarConfiguracion={guardarConfiguracionCotizacion}
            alUsarConfiguracionGlobal={usarConfiguracionGlobal}
            alRecalcularConMatricesVigentes={recalcularConMatricesVigentes}
            variacionMensual={variacionMensual}
            ciudades={ciudades}
        />
        {pdf.modalPdf}
        </>
    );
};
